import { useEffect, useState } from "react";
import { useInput, UseInput } from "../../../hooks/useInput";
import { getDefaultInputValue } from "../../../utility/form";
import { renderConditionalProp } from "../../../utility/utils";
import classes from "./Input.module.scss";

export interface Options {
  val: string;
  label: string;
}

export interface InputDTO {
  label: string;
  val: string;
  validationMessage: string;
  key: string;
  extraProp?: any;
  type?: string;
  options?: Options[];
  fields?: InputDTO[];
  onChange?: (e: Event, input: UseInput) => void;
  // renders a native <input> instead of ft-textfield
  native?: boolean;
  // native-mode only: owns custom validity and calls checkValidity before returning its result
  onValidate?: (el: HTMLInputElement) => boolean;
}

export interface InputProps {
  val: any;
  label: string;
  validationMessage: string;
  type?: string;
  passState: Function;
  id: string;
  extraProp?: any;
  options?: Options[];
  datacy?: string;
  onChange?: (e: Event, input: UseInput) => void;
  native?: boolean;
  onValidate?: (el: HTMLInputElement) => boolean;
}

export const Input: React.FC<InputProps> = ({
  val,
  label,
  validationMessage,
  type,
  passState,
  extraProp,
  id,
  options,
  datacy,
  onChange,
  native,
  onValidate,
}) => {
  const input: UseInput = useInput(getDefaultInputValue(val));
  const [nativeErrorMessage, setNativeErrorMessage] = useState("");
  useEffect(() => {
    passState(id, input);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.value, id, passState]);

  const handleInputChange = (e: Event) => {
    if (onChange) {
      onChange(e, input);
    } else {
      input.valueChangeHandler(e);
    }
  };

  const handleBlur = (e: Event) => {
    input.valueChangeHandler(e);
    input.inputBlurHandler(e);
  };

  const handleNativeBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    handleBlur(e.nativeEvent);
    const el = e.currentTarget;
    el.setCustomValidity("");
    if (onValidate) {
      onValidate(el);
    } else if (!el.validity.valid) {
      el.setCustomValidity(validationMessage);
      el.checkValidity();
    } else {
      el.checkValidity();
    }
  };

  useEffect(() => {
    if (!native) return;
    const el = input?.ref?.current;
    if (!el) return;

    const handleInvalid = () => setNativeErrorMessage(el.validationMessage);
    const handleValueChange = () => {
      el.setCustomValidity("");
      setNativeErrorMessage("");
    };

    el.addEventListener("invalid", handleInvalid);
    el.addEventListener("input", handleValueChange);
    return () => {
      el.removeEventListener("invalid", handleInvalid);
      el.removeEventListener("input", handleValueChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [native, input?.ref?.current]);

  useEffect(() => {
    if (type === "select") {
      const currentInput = input?.ref?.current;
      const tempChangeHandler = (event: any) => {
        input.valueChangeHandler(event);
      };
      if (currentInput) {
        currentInput.removeEventListener("selected", tempChangeHandler);
        currentInput.addEventListener("selected", tempChangeHandler);
      }
      return () => {
        if (currentInput) {
          currentInput.removeEventListener("selected", tempChangeHandler);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderOptions = (options: Options[]) => {
    return options.map((option: Options, i) => {
      return (
        <ft-list-item
          key={i}
          {...renderConditionalProp(input.value === option.val, "selected")}
          value={option.val}
        >
          {option.label}
        </ft-list-item>
      );
    });
  };

  if (type === "select") {
    return (
      <ft-select
        validationMessage={validationMessage}
        value={input.value}
        outlined
        label={label}
        data-testid={datacy}
        id="payment-type"
        class={classes.Main}
        ref={input.ref}
        {...extraProp}
      >
        {options ? renderOptions(options) : null}
      </ft-select>
    );
  }
  if (native) {
    return (
      <div
        className={`${classes.NativeField} ${nativeErrorMessage ? classes.NativeFieldHasError : ""}`}
      >
        <input
          id={id}
          ref={input.ref}
          className={classes.NativeInput}
          type={type ? type : "text"}
          placeholder=" "
          data-testid={datacy}
          onInput={handleInputChange}
          onBlur={handleNativeBlur}
          value={input.value}
          {...extraProp}
        />
        <label htmlFor={id} className={classes.NativeLabel}>
          {label}
        </label>
        <div
          className={`${classes.NativeError} ${nativeErrorMessage ? classes.NativeErrorVisible : ""}`}
          aria-live="polite"
          aria-hidden={!nativeErrorMessage}
        >
          {nativeErrorMessage || "\u00a0"}
        </div>
      </div>
    );
  }

  return (
    <ft-textfield
      class={classes.Main}
      ref={input.ref}
      outlined
      label={label}
      data-testid={datacy}
      validationMessage={validationMessage}
      type={type ? type : "text"}
      onInput={handleInputChange}
      value={input.value}
      onBlur={handleBlur}
      {...extraProp}
    ></ft-textfield>
  );
};
